/**
 * VotesScreen — lista de votaciones + ver detalle / votar.
 * A = abrir detalle (votación), B = back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { votesApi, type Vote, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { BallotSprite, LoadingSprite } from '@/components/Sprites';

export function VotesScreen() {
  const { goto, openDetail } = useApp();
  const [items, setItems] = useState<Vote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const handlersRef = useRef({ items, selected, openDetail });
  handlersRef.current = { items, selected, openDetail };

  const load = useCallback(async () => {
    try {
      const res = await votesApi.list();
      setItems(res.votes || []);
    } catch (err) {
      if (err instanceof ApiError) console.error('Votes load:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = (delta: number) => {
    if (items.length === 0) return;
    const next = Math.max(0, Math.min(items.length - 1, selected + delta));
    if (next !== selected) {
      setSelected(next);
      sfx.tick();
    }
  };

  useEffect(() => {
    const onUp = () => move(-1);
    const onDown = () => move(1);
    const onA = () => {
      const h = handlersRef.current;
      const item = h.items[h.selected];
      if (item) { sfx.confirm(); h.openDetail({ id: item.id, type: 'vote' }); }
    };
    const onB = () => { sfx.cancel(); goto('home'); };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [goto]);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <BallotSprite size={11} />
          <span className="gb-pix gb-pix-xs">VOTOS · {items.length}</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
            [A VER]
          </span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '0', gap: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-xs gb-mid-text">CARGANDO...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <BallotSprite size={48} />
            <div className="gb-pix gb-pix-xs gb-mid-text">SIN VOTACIONES</div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, maxWidth: 200, textAlign: 'center' }}>
              Las decisiones importantes se votan acá.
            </div>
          </div>
        ) : (
          <div className="gb-list" style={{ overflowY: 'auto', flex: 1 }}>
            {items.map((v, i) => {
              const sel = i === selected;
              const total = v.total_votes ?? v.options.reduce((s, o) => s + o.votes, 0);
              const voted = (v.my_vote !== null && v.my_vote !== undefined) || v.has_voted === true;
              const status = voted ? 'VOTADO' : (v.is_ended ? 'CERRADA' : 'ABIERTA');
              const statusVariant = voted ? 'success' : v.is_ended ? 'pending' : 'info';
              return (
                <button
                  key={v.id}
                  className="gb-list-item"
                  data-selected={sel}
                  onClick={() => { setSelected(i); sfx.confirm(); openDetail({ id: v.id, type: 'vote' }); }}
                  onMouseEnter={() => setSelected(i)}
                  data-testid={`vote-${v.id}`}
                >
                  <div className="gb-list-row">
                    <span className={`gb-list-badge gb-list-badge--${statusVariant}`} style={{ fontSize: 4 }}>
                      {status}
                    </span>
                    <span className="gb-list-title">{v.title}</span>
                  </div>
                  <div className="gb-list-row gb-list-meta">
                    <span>{v.options.length} opciones · {total} votos</span>
                    <span>· cierra {formatRelative(v.ends_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}